import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { escalationsApi, type EscalationRow, type StoreEscalationInput } from '../../lib/api/escalations';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './EscalationFormDialog.module.css';

const FORBIDDEN_CODE = 'forbidden';

/**
 * The create-escalation form — `POST /escalations` (M-10, Task 5, spec E-1).
 *
 * ## `case_number` is free text, never a picker (spec E-1's own point)
 *
 * `StoreEscalationRequest` deliberately never checks `case_number` against
 * `cases.case_number` as a foreign key — a match links `case_id` server-side,
 * no match keeps the text (`EscalationController::store()`'s own docblock).
 * A `Combobox` bound to the case catalogue here would reintroduce exactly
 * the refusal spec E-1 exists to remove: a complaint phoned in before a case
 * has ever been opened. A plain `Input` is therefore the correct control,
 * not an unfinished one.
 *
 * ## Only `mobile` is required
 *
 * `case_number`/`reason`/`description` are all optional (`StoreEscalationRequest::
 * rules()`) — a complaint may carry only a phone number and nothing else
 * yet, the same "captured now, filled in later" shape the legacy form
 * allowed.
 */

export interface EscalationFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (created: EscalationRow) => void;
}

interface FormState {
  mobile: string;
  caseNumber: string;
  reason: string;
  description: string;
}

const BLANK: FormState = { mobile: '', caseNumber: '', reason: '', description: '' };

export function EscalationFormDialog({ open, onClose, onSaved }: EscalationFormDialogProps) {
  const { t } = useTranslation();

  const [state, setState] = useState<FormState>(BLANK);
  const [missingMobile, setMissingMobile] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const mobileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setState(BLANK);
    setMissingMobile(false);
    setFieldErrors({});
    setFailure(null);
  }, [open]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('escalations.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('escalations.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('escalations.createFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    if (state.mobile.trim() === '') {
      setMissingMobile(true);
      return;
    }
    setMissingMobile(false);

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setFailure(null);

    try {
      const caseNumber = state.caseNumber.trim();
      const reason = state.reason.trim();
      const description = state.description.trim();

      const input: StoreEscalationInput = {
        mobile: state.mobile.trim(),
        case_number: caseNumber === '' ? undefined : caseNumber,
        reason: reason === '' ? undefined : reason,
        description: description === '' ? undefined : description,
      };

      const created = await escalationsApi.create(input);
      onSaved(created);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      kind="form"
      onClose={onClose}
      initialFocusRef={mobileRef}
      title={t('escalations.createTitle')}
    >
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <Input
          ref={mobileRef}
          label={t('form.mobileLabel')}
          width="phone"
          dir="ltr"
          placeholder={t('form.mobilePlaceholder')}
          required
          value={state.mobile}
          error={missingMobile ? t('escalations.required') : fieldErrors['mobile']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, mobile: value }));
          }}
        />

        <Input
          label={t('table.caseNumber')}
          width="compact"
          dir="ltr"
          value={state.caseNumber}
          error={fieldErrors['case_number']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, caseNumber: value }));
          }}
        />

        <Input
          label={t('escalations.fieldReason')}
          width="fill"
          value={state.reason}
          error={fieldErrors['reason']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, reason: value }));
          }}
        />

        <Textarea
          label={t('escalations.fieldDescription')}
          dir="auto"
          value={state.description}
          error={fieldErrors['description']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, description: value }));
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<FloppyDiskIcon size={16} />}>
            {t('escalations.createSubmit')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('escalations.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
